/**
 * The Rust marching-cubes kernel, as the viewer calls it.
 *
 * Loaded through the registration loader, which already resolves the package in
 * all four builds - extension host, webview, standalone page and Node. The
 * extraction itself is `wasm/pointcloud-parser/src/volume/`.
 */
import { loadRegistrationWasm } from '../registration/wasmLoader';
/* eslint-enable @typescript-eslint/naming-convention */
let loaded = null;
function loadVolumeWasm() {
    if (!loaded) {
        loaded = loadRegistrationWasm().then(wasm => {
            const candidate = wasm;
            return candidate && typeof candidate.extract_isosurface === 'function' ? candidate : null;
        });
    }
    return loaded;
}
/**
 * Extract an isosurface. Throws when the crate is unavailable or the volume is
 * too small for the requested decimation - there is no JavaScript marching
 * cubes to fall back to.
 */
export async function extractIsosurfaceWasm(samples, sizes, ijkToWorld, threshold, step, maxTriangles) {
    const wasm = await loadVolumeWasm();
    if (!wasm) {
        throw new Error('Isosurface extraction requires the Rust/WASM kernel');
    }
    // Volumes arrive in whatever type the file carried (16-bit CT, 8-bit
    // microscopy); the kernel works in f32, so anything else is converted once
    // here rather than branching per sample inside the loop.
    const values = samples instanceof Float32Array ? samples : Float32Array.from(samples);
    const raw = wasm.extract_isosurface(values, Uint32Array.from(sizes), Float64Array.from(ijkToWorld), threshold, Uint32Array.from(step), maxTriangles);
    const used = Array.from(raw.step);
    const mesh = {
        vertexCount: raw.vertex_count,
        triangleCount: raw.triangle_count,
        positions: raw.take_positions(),
        normals: raw.take_normals(),
        indices: raw.take_indices(),
        gradientMagnitudes: raw.take_gradient_magnitudes(),
        step: used,
    };
    raw.free?.();
    return mesh;
}
/**
 * Read a NRRD volume. `detached` carries the payload when the header names a
 * separate data file; pass an empty array otherwise.
 */
export async function parseNrrdWasm(data, detached = new Uint8Array(0)) {
    const wasm = (await loadVolumeWasm());
    if (!wasm || typeof wasm.parse_nrrd !== 'function') {
        throw new Error('Reading NRRD requires the Rust/WASM kernel');
    }
    const raw = wasm.parse_nrrd(data, detached);
    const volume = {
        sizes: Array.from(raw.sizes),
        ijkToWorld: Array.from(raw.ijk_to_world),
        channels: raw.channels,
        header: JSON.parse(raw.metadata_json),
        samples: raw.take_samples(),
    };
    raw.free?.();
    return volume;
}
/** Build the exposed shell of every retained voxel. */
export async function buildVolumeVoxelsWasm(samples, sizes, ijkToWorld, threshold, step, maxFaces, clip, brightness) {
    const wasm = (await loadVolumeWasm());
    if (!wasm || typeof wasm.build_volume_voxels !== 'function') {
        throw new Error('Building voxels requires the Rust/WASM kernel');
    }
    const values = samples instanceof Float32Array ? samples : Float32Array.from(samples);
    const raw = wasm.build_volume_voxels(values, Uint32Array.from(sizes), Float64Array.from(ijkToWorld), threshold, Uint32Array.from(step), maxFaces, Uint32Array.from(clip), brightness.mode, brightness.windowCenter, brightness.windowWidth, brightness.sliceRanges, brightness.volumeRange, brightness.monochrome1);
    const mesh = {
        vertexCount: raw.vertex_count,
        faceCount: raw.face_count,
        voxelCount: raw.voxel_count,
        step: Array.from(raw.step),
        voxelSize: Array.from(raw.voxel_size),
        positions: raw.take_positions(),
        colors: raw.take_colors(),
        intensity: raw.take_intensity(),
        indices: raw.take_indices(),
    };
    raw.free?.();
    return mesh;
}
